import cx from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';

import { Order } from 'types';

import './CountdownTimer.style.scss';

type Props = {
  order: Order;
  withPlaceholder?: boolean;
  refetchOrderStatus: () => void;
};

const DEFAULT_COLOR = '#fff';
const TIMER_SIZE = 90;

export const CountdownTimer: React.FC<Props> = ({
  order,
  refetchOrderStatus,
}) => {
  const { statusAt, statusTimeoutAt } = useMemo(() => order || {}, [order]);
  const timerTotalSeconds = useMemo(
    () =>
      (new Date(statusTimeoutAt).getTime() - new Date(statusAt).getTime()) /
      1000,
    [statusAt, statusTimeoutAt],
  );
  const timeUntilTimeoutInSeconds = useMemo(
    () => Math.round((new Date(statusTimeoutAt).getTime() - Date.now()) / 1000),
    [statusTimeoutAt],
  );

  const splitTime = useCallback(
    (seconds: number) => ({
      hours: Math.floor(seconds / 3600),
      minutes: Math.floor((seconds % 3600) / 60),
      seconds: Math.floor(seconds % 60),
    }),
    [],
  );

  const roundTime = useCallback(
    (time: number) => String(time).padStart(2, '0'),
    [],
  );

  const formatTime = useCallback(
    (time: number) => {
      const { hours, minutes, seconds } = splitTime(time);
      const hh = hours ? `${roundTime(hours)}:` : '';
      return `${hh}${roundTime(minutes)}:${roundTime(seconds)}`;
    },
    [roundTime, splitTime],
  );

  return (
    <>
      <div
        className={cx(
          'timer-wrapper tw-block tw-w-16 tw-h-16 tw-mr-4 md:tw-w-24 md:tw-h-24 md:tw-mr-8',
        )}
      >
        <CountdownCircleTimer
          isPlaying
          initialRemainingTime={timeUntilTimeoutInSeconds}
          duration={timerTotalSeconds}
          colors={DEFAULT_COLOR}
          size={TIMER_SIZE}
          rotation="counterclockwise"
          trailColor={DEFAULT_COLOR}
          onComplete={refetchOrderStatus}
        >
          {({ remainingTime }) => (
            <div className="tw-text-sm tw-text-countdown md:tw-text-lg">
              {formatTime(remainingTime)}
            </div>
          )}
        </CountdownCircleTimer>
      </div>
    </>
  );
};
